import { useMemo } from 'react';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import { useAdminContext } from '../context/AdminContext';

/**
 * Hook for role-based access control in admin dashboard
 * Provides permission checks and access control for admin functionality
 * 
 * @returns {Object} Admin permissions and access control functions
 */
function useAdminPermissions() {
  const {
    isLoggedIn,
    roles,
    isAnyAdmin,
    isStudentEmployee,
    user
  } = useRoleAuthentication();

  const { isAdminView, adminUser } = useAdminContext();

  // Compute admin permissions using the boolean flags from useRoleAuthentication
  const permissions = useMemo(() => {
    return {
      // Basic admin access
      canAccessAdminDashboard: isLoggedIn && (isAnyAdmin || isStudentEmployee),

      // Student dashboard viewing permissions - all admin types can view
      canViewStudentDashboards: isLoggedIn && (isAnyAdmin || isStudentEmployee),

      // All admin types can view all students (no program restrictions)
      canViewAllStudents: isLoggedIn && (isAnyAdmin || isStudentEmployee),

      // Action permissions - exclude student employees from admin actions
      canPerformAdminActions: isLoggedIn && isAnyAdmin,

      // Search permissions - all admin types can search
      canSearchStudents: isLoggedIn && (isAnyAdmin || isStudentEmployee),

      // Audit permissions - only full admins
      canViewAuditLogs: isLoggedIn && isAnyAdmin
    };
  }, [isLoggedIn, isAnyAdmin, isStudentEmployee]);

  // All admin types can access all programs (no restrictions)
  const getAllowedPrograms = useMemo(() => {
    // If user has any admin role, they can view all programs
    if (isAnyAdmin || isStudentEmployee) {
      return ['honors', 'scholars'];
    }

    return [];
  }, [isAnyAdmin, isStudentEmployee]);

  // Permission check functions
  const checkPermission = (permission) => {
    return permissions[permission] || false;
  };

  const canAccessStudent = (studentPrograms = []) => {
    // All admin types can view all students regardless of program
    return permissions.canViewStudentDashboards;
  };

  const requiresAuthentication = () => {
    if (!isLoggedIn) {
      throw new Error('Authentication required for admin access');
    }
  };

  const requiresAdminAccess = () => {
    requiresAuthentication();

    if (!permissions.canAccessAdminDashboard) {
      throw new Error('Admin access required');
    }
  };

  const requiresStudentViewAccess = () => {
    requiresAdminAccess();

    if (!permissions.canViewStudentDashboards) {
      throw new Error('Student dashboard view access required');
    }
  };

  return {
    // Permission flags
    ...permissions,

    // Computed properties
    allowedPrograms: getAllowedPrograms,
    isAuthenticated: isLoggedIn,
    isInAdminView: isAdminView,

    // Role flags (from useRoleAuthentication)
    isAnyAdmin,
    isStudentEmployee,

    // Permission check functions
    checkPermission,
    canAccessStudent,

    // Requirement functions (throw errors if not met)
    requiresAuthentication,
    requiresAdminAccess,
    requiresStudentViewAccess,

    // User info
    adminUser: adminUser || user,
    userRoles: roles || []
  };
}

export default useAdminPermissions;